import React, { useState, useEffect, useRef } from 'react';

/**
 * Composant d'image optimisée avec lazy loading et srcset
 * Améliore les performances en chargeant les images uniquement quand nécessaire
 *
 * @param {string} src - URL de l'image (format original)
 * @param {string} alt - Texte alternatif descriptif
 * @param {string} className - Classes CSS supplémentaires
 * @param {string} sizes - Attribut sizes pour responsive images
 * @param {number} width - Largeur de l'image
 * @param {number} height - Hauteur de l'image
 * @param {string} loading - "lazy" (défaut) ou "eager"
 * @param {function} onLoad - Callback appelé quand l'image est chargée
 */

function OptimizedImage({
  src,
  alt = '',
  className = '',
  sizes = '100vw',
  width,
  height,
  loading = 'lazy',
  onLoad,
  ...props
}) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const imgRef = useRef(null);

  // Générer le srcset pour différentes tailles
  const generateSrcSet = (imageSrc) => {
    if (!imageSrc) return '';

    // Si l'image vient d'Unsplash, utiliser les paramètres de resize
    if (imageSrc.includes('unsplash.com')) {
      const widths = [320, 640, 768, 1024, 1280, 1920];
      return widths
        .map(w => `${imageSrc}&w=${w} ${w}w`)
        .join(', ');
    }

    // Pour les images locales, retourner l'image originale
    // Dans une vraie app, on aurait plusieurs versions pré-générées
    return '';
  };

  // Convertir l'URL en WebP si disponible
  const getWebPUrl = (imageSrc) => {
    if (!imageSrc) return '';

    // Pour Unsplash, forcer le format WebP
    if (imageSrc.includes('unsplash.com')) {
      return `${imageSrc}&fm=webp&q=85`;
    }

    // Pour les images locales importées par webpack (contiennent /static/media/)
    // Ne pas essayer de convertir en WebP car webpack gère déjà l'optimisation
    if (imageSrc.includes('/static/media/') || imageSrc.startsWith('data:')) {
      return ''; // Pas de version WebP pour les images webpack
    }

    // Pour les autres images, essayer de remplacer l'extension
    return imageSrc.replace(/\.(jpg|jpeg|png)$/i, '.webp');
  };

  // Intersection Observer pour lazy loading
  useEffect(() => {
    // Toujours afficher l'image immédiatement
    setIsInView(true);

    // Garder le code lazy loading pour référence future si besoin
    /*
    if (loading !== 'lazy') {
      setIsInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            observer.disconnect();
          }
        });
      },
      {
        rootMargin: '50px',
      }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => {
      if (imgRef.current) {
        observer.unobserve(imgRef.current);
      }
    };
    */
  }, [loading]);

  const handleLoad = () => {
    setIsLoaded(true);
    if (onLoad) {
      onLoad();
    }
  };

  const srcset = generateSrcSet(src);
  const webpSrc = getWebPUrl(src);

  return (
    <>
      {/* Image principale avec srcset et WebP */}
      {isInView ? (
        <picture>
          {/* Source WebP pour navigateurs compatibles */}
          {webpSrc && (
            <source
              type="image/webp"
              srcSet={srcset || webpSrc}
              sizes={sizes}
            />
          )}

          {/* Fallback image classique */}
          <img
            ref={imgRef}
            src={src}
            srcSet={srcset}
            sizes={sizes}
            alt={alt}
            width={width}
            height={height}
            loading={loading}
            onLoad={handleLoad}
            className={className}
            draggable={false}
            onContextMenu={(e) => e.preventDefault()}
            {...props}
          />
        </picture>
      ) : (
        <img
          ref={imgRef}
          src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1 1'%3E%3C/svg%3E"
          alt=""
          className={className}
          style={{
            background: 'linear-gradient(135deg, #e5e7eb 0%, #d1d5db 100%)',
            ...props.style,
          }}
        />
      )}
    </>
  );
}

export default OptimizedImage;
